import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import {
  fetchUnits,
  fetchContacts,
  createUnit,
  updateUnit,
  deleteUnit,
  setUnitPayer,
  setActiveUnit,
} from '../../store/slices/financeSlice'
import type { Unit, UnitCreateInput, UnitUpdateInput, PaymentMethod, PaymentFrequency, ContactRole, UnitContact } from '@/types/finance'
import { addUnitContact, updateUnitContact, removeUnitContact, getUnit } from '../../services/api/finance'
import { useToast } from '../../components/ToastContainer'
import { useFinancePageSize, FINANCE_PAGE_SIZE_OPTIONS } from '../../hooks/useFinancePageSize'
import usePermissions, { Permission } from '../../hooks/usePermissions'
import { FinanceAuditLog } from '../../components/finance'
import {
  BuildingOfficeIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'

interface UnitFormData {
  unitCode: string
  building: string
  floor: string
  unitNumber: string
  address: string
  pingArea: string
  paymentMethod: PaymentMethod
  paymentFrequency: PaymentFrequency
  payerId: string
}

const initialFormData: UnitFormData = {
  unitCode: '',
  building: '',
  floor: '',
  unitNumber: '',
  address: '',
  pingArea: '',
  paymentMethod: 'PAPER',
  paymentFrequency: 'MONTHLY',
  payerId: '',
}

const paymentMethodLabels: Record<PaymentMethod, string> = {
  PAPER: '紙本帳單',
  EMAIL: 'Email帳單',
  ACH: '自動扣款',
  FAX: '傳真帳單',
  POSTAL_MAIL: '郵寄帳單',
}

const paymentFrequencyLabels: Record<PaymentFrequency, string> = {
  MONTHLY: '月繳',
  QUARTERLY: '季繳',
  YEARLY: '年繳',
}

const ROLE_LABELS: Record<ContactRole, { label: string; className: string }> = {
  OWNER: { label: '所有權人', className: 'bg-indigo-100 text-indigo-800' },
  TENANT: { label: '租客', className: 'bg-amber-100 text-amber-800' },
  FAMILY: { label: '家人', className: 'bg-green-100 text-green-800' },
  OTHER: { label: '其他', className: 'bg-gray-100 text-gray-700' },
}
const ROLE_OPTIONS: ContactRole[] = ['OWNER', 'TENANT', 'FAMILY', 'OTHER']

const UnitList = () => {
  const { projectKey } = useParams<{ projectKey: string }>()
  const dispatch = useAppDispatch()
  const { showSuccess, showError } = useToast()
  const { currentProject } = useAppSelector(state => state.project)
  const {
    units,
    unitsPagination,
    unitsLoading,
    unitsError,
    activeUnit,
    contacts,
  } = useAppSelector(state => state.finance)

  const { hasPermission } = usePermissions(currentProject?.id)
  const canView = hasPermission(Permission.VIEW_FINANCE_UNITS)
  const canManage = hasPermission(Permission.MANAGE_FINANCE_UNITS)
  const canDelete = hasPermission(Permission.DELETE_FINANCE_UNITS)

  const [search, setSearch] = useState('')
  const [building, setBuilding] = useState('')
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<PaymentMethod | ''>('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useFinancePageSize()
  const [showModal, setShowModal] = useState(false)
  // payer 選擇器（嵌入在編輯 modal）
  const [payerSearch, setPayerSearch] = useState('')
  const [payerMutating, setPayerMutating] = useState(false)
  // 戶別 × 聯絡人 關聯管理（嵌入編輯 modal）
  const [contactsUnit, setContactsUnit] = useState<Unit | null>(null)
  const [contactsUnitLoading, setContactsUnitLoading] = useState(false)
  const [contactSearch, setContactSearch] = useState('')
  const [newContactId, setNewContactId] = useState('')
  const [newContactRole, setNewContactRole] = useState<ContactRole>('OWNER')
  const [newLeaseStart, setNewLeaseStart] = useState('')
  const [newLeaseEnd, setNewLeaseEnd] = useState('')
  const [contactMutating, setContactMutating] = useState(false)
  // 編輯既有 UnitContact 的租約期間
  const [leaseEditingId, setLeaseEditingId] = useState<string>('')
  const [leaseEditStart, setLeaseEditStart] = useState('')
  const [leaseEditEnd, setLeaseEditEnd] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<UnitFormData>(initialFormData)
  const [submitting, setSubmitting] = useState(false)
  const [editReadOnly, setEditReadOnly] = useState(false)

  useEffect(() => {
    if (currentProject?.id) {
      dispatch(fetchUnits({
        projectId: currentProject.id,
        params: {
          page,
          limit: pageSize,
          search: search || undefined,
          building: building || undefined,
          paymentMethod: paymentMethodFilter || undefined,
        },
      }))
      // Also fetch contacts for the payer dropdown
      dispatch(fetchContacts({ projectId: currentProject.id, params: { limit: 1000 } }))
    }
  }, [dispatch, currentProject?.id, page, pageSize, search, building, paymentMethodFilter])

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const openCreateModal = () => {
    setFormData(initialFormData)
    setIsEditing(false)
    setShowModal(true)
  }

  const openEditModal = async (unit: Unit, readOnly = false) => {
    setFormData({
      unitCode: unit.unitCode,
      building: unit.building,
      floor: unit.floor,
      unitNumber: unit.unitNumber,
      address: unit.address,
      pingArea: unit.pingArea.toString(),
      paymentMethod: unit.paymentMethod,
      paymentFrequency: unit.paymentFrequency,
      payerId: unit.payerId || '',
    })
    dispatch(setActiveUnit(unit))
    setIsEditing(true)
    setEditReadOnly(readOnly)
    setShowModal(true)
    // 同步載入關聯聯絡人資料，讓編輯 modal 底部可直接管理
    setContactsUnit(unit)
    setContactSearch('')
    setNewContactId('')
    setNewContactRole('OWNER')
    await loadUnitWithContacts(unit.id)
    if (currentProject?.id && contacts.length === 0) {
      dispatch(fetchContacts({ projectId: currentProject.id, params: { page: 1, limit: 2000 } }))
    }
  }

  // ===== 聯絡人關聯管理 =====
  const loadUnitWithContacts = async (unitId: string) => {
    if (!currentProject?.id) return
    setContactsUnitLoading(true)
    try {
      const res = await getUnit(currentProject.id, unitId)
      if (res.success && res.data) {
        setContactsUnit(res.data.unit)
      }
    } catch (error: any) {
      showError('載入失敗', error.message || '無法載入戶別資料')
    } finally {
      setContactsUnitLoading(false)
    }
  }

  const handleAddUnitContact = async () => {
    if (!currentProject?.id || !contactsUnit) return
    if (!newContactId) {
      showError('請選擇聯絡人')
      return
    }
    const already = contactsUnit.unitContacts?.some(
      uc => uc.contactId === newContactId && uc.role === newContactRole
    )
    if (already) {
      showError('此聯絡人已有相同角色的關聯')
      return
    }
    if (newContactRole === 'TENANT') {
      if (!newLeaseStart || !newLeaseEnd) {
        showError('租客關聯必須填寫租約起訖日期')
        return
      }
      if (newLeaseStart > newLeaseEnd) {
        showError('租約起始日不可晚於結束日')
        return
      }
    }
    setContactMutating(true)
    try {
      await addUnitContact(currentProject.id, contactsUnit.id, {
        contactId: newContactId,
        role: newContactRole,
        leaseStartDate: newContactRole === 'TENANT' && newLeaseStart ? newLeaseStart : null,
        leaseEndDate: newContactRole === 'TENANT' && newLeaseEnd ? newLeaseEnd : null,
      })
      showSuccess('已新增聯絡人關聯')
      await loadUnitWithContacts(contactsUnit.id)
      setNewContactId('')
      setContactSearch('')
      setNewLeaseStart('')
      setNewLeaseEnd('')
      // 刷新列表以更新角色聚合
      if (currentProject?.id) {
        dispatch(fetchUnits({
          projectId: currentProject.id,
          params: {
            page,
            limit: pageSize,
            search: search || undefined,
            building: building || undefined,
            paymentMethod: paymentMethodFilter || undefined,
          },
        }))
      }
    } catch (error: any) {
      showError('新增失敗', error.message || '請稍後再試')
    } finally {
      setContactMutating(false)
    }
  }

  const handleRemoveUnitContact = async (unitContact: UnitContact) => {
    if (!currentProject?.id || !contactsUnit) return
    if (!confirm(`確定要移除「${unitContact.contact?.name || '聯絡人'}」的關聯嗎？`)) return
    setContactMutating(true)
    try {
      await removeUnitContact(currentProject.id, contactsUnit.id, unitContact.id)
      showSuccess('已移除聯絡人關聯')
      await loadUnitWithContacts(contactsUnit.id)
      if (currentProject?.id) {
        dispatch(fetchUnits({
          projectId: currentProject.id,
          params: {
            page,
            limit: pageSize,
            search: search || undefined,
            building: building || undefined,
            paymentMethod: paymentMethodFilter || undefined,
          },
        }))
      }
    } catch (error: any) {
      showError('移除失敗', error.message || '請稍後再試')
    } finally {
      setContactMutating(false)
    }
  }

  const beginEditLease = (uc: UnitContact) => {
    setLeaseEditingId(uc.id)
    setLeaseEditStart(uc.leaseStartDate ? String(uc.leaseStartDate).slice(0, 10) : '')
    setLeaseEditEnd(uc.leaseEndDate ? String(uc.leaseEndDate).slice(0, 10) : '')
  }
  const cancelEditLease = () => {
    setLeaseEditingId('')
    setLeaseEditStart('')
    setLeaseEditEnd('')
  }
  const saveEditLease = async (uc: UnitContact) => {
    if (!currentProject?.id || !contactsUnit) return
    if (!leaseEditStart || !leaseEditEnd) {
      showError('請填寫完整的租約起訖日期')
      return
    }
    if (leaseEditStart > leaseEditEnd) {
      showError('租約起始日不可晚於結束日')
      return
    }
    setContactMutating(true)
    try {
      await updateUnitContact(currentProject.id, contactsUnit.id, uc.id, {
        leaseStartDate: leaseEditStart,
        leaseEndDate: leaseEditEnd,
      })
      showSuccess('租約期間已更新')
      cancelEditLease()
      await loadUnitWithContacts(contactsUnit.id)
    } catch (error: any) {
      showError('更新失敗', error.message || '請稍後再試')
    } finally {
      setContactMutating(false)
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setEditReadOnly(false)
    dispatch(setActiveUnit(null))
    setFormData(initialFormData)
    // 清掉嵌入式關聯區塊狀態
    setContactsUnit(null)
    setContactSearch('')
    setNewContactId('')
    setNewContactRole('OWNER')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentProject?.id) return

    if (!formData.unitCode.trim() || !formData.building.trim() || !formData.floor.trim() || !formData.address.trim() || !formData.pingArea) {
      showError('請填寫必填欄位（單位代碼、棟別、樓層、地址、坪數）')
      return
    }

    setSubmitting(true)
    try {
      const data = {
        unitCode: formData.unitCode,
        building: formData.building,
        floor: formData.floor,
        unitNumber: formData.unitNumber,
        address: formData.address,
        pingArea: parseFloat(formData.pingArea) || 0,
        paymentMethod: formData.paymentMethod,
        paymentFrequency: formData.paymentFrequency,
        payerId: formData.payerId || undefined,
      }

      if (isEditing && activeUnit) {
        await dispatch(updateUnit({
          projectId: currentProject.id,
          unitId: activeUnit.id,
          data: data as UnitUpdateInput,
        })).unwrap()
        showSuccess('單位更新成功')
      } else {
        await dispatch(createUnit({
          projectId: currentProject.id,
          data: data as UnitCreateInput,
        })).unwrap()
        showSuccess('單位建立成功')
      }
      closeModal()
    } catch (error: any) {
      showError(isEditing ? '更新失敗' : '建立失敗', error.message || '請稍後再試')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSetPayer = async (payerId: string | null) => {
    if (!currentProject?.id || !activeUnit) return
    setPayerMutating(true)
    try {
      await dispatch(setUnitPayer({
        projectId: currentProject.id,
        unitId: activeUnit.id,
        payerId,
      })).unwrap()
      showSuccess(payerId ? '繳費人設定成功' : '繳費人已清除')
      setPayerSearch('')
      // 刷新 modal 內的 unit 資料，讓畫面反映最新 payer
      await loadUnitWithContacts(activeUnit.id)
      // 同步 formData.payerId，其他欄位一起送出更新時不會被覆寫
      setFormData(prev => ({ ...prev, payerId: payerId || '' }))
      // 刷新列表資料（區權人欄位不受影響，但確保 state 同步）
      if (currentProject?.id) {
        dispatch(fetchUnits({
          projectId: currentProject.id,
          params: {
            page,
            limit: pageSize,
            search: search || undefined,
            building: building || undefined,
            paymentMethod: paymentMethodFilter || undefined,
          },
        }))
      }
    } catch (error: any) {
      showError('設定失敗', error.message || '請稍後再試')
    } finally {
      setPayerMutating(false)
    }
  }

  const handleDelete = async (unit: Unit) => {
    if (!currentProject?.id) return
    if (!confirm(`確定要刪除單位「${unit.unitCode}」嗎？`)) return

    try {
      await dispatch(deleteUnit({
        projectId: currentProject.id,
        unitId: unit.id,
      })).unwrap()
      showSuccess('單位已刪除')
    } catch (error: any) {
      showError('刪除失敗', error.message || '請稍後再試')
    }
  }

  // Get unique buildings for filter
  const uniqueBuildings = [...new Set(units.map(u => u.building))].sort()

  if (!canView) {
    return (
      <div className="p-6 max-w-2xl mx-auto mt-12 text-center bg-white rounded-lg shadow">
        <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">無權限存取</h2>
        <p className="mt-2 text-sm text-gray-600">您沒有檢視房屋單位管理的權限，請聯絡專案管理員。</p>
        <Link to={`/project/${projectKey}/finance`} className="inline-block mt-4 text-blue-600 hover:underline">回財務管理首頁</Link>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/project/${projectKey}/finance`}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div className="flex items-center space-x-3">
            <BuildingOfficeIcon className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">房屋單位管理</h1>
              <p className="text-sm text-gray-500">管理房屋單位與繳費設定</p>
            </div>
          </div>
        </div>

        {canManage && (
          <button
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            新增單位
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="搜尋單位代碼、地址..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <select
              value={building}
              onChange={(e) => { setBuilding(e.target.value); setPage(1) }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">全部棟別</option>
              {uniqueBuildings.map(b => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={paymentMethodFilter}
              onChange={(e) => { setPaymentMethodFilter(e.target.value as PaymentMethod | ''); setPage(1) }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">全部繳費方式</option>
              {Object.entries(paymentMethodLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {unitsError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-700">{unitsError}</p>
        </div>
      )}

      {/* Units Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Top pagination */}
        {unitsPagination && (
          <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-y-2">
            <div className="text-sm text-gray-700">
              共 {unitsPagination.total} 筆，第 {unitsPagination.page} / {unitsPagination.totalPages || 1} 頁
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1">
                <label className="text-sm text-gray-500">每頁</label>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  {FINANCE_PAGE_SIZE_OPTIONS.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span className="text-sm text-gray-500">筆</span>
              </div>
              {unitsPagination.totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(unitsPagination.totalPages, p + 1))}
                    disabled={page === unitsPagination.totalPages}
                    className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {unitsLoading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : units.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            {search || building || paymentMethodFilter ? '找不到符合條件的單位' : '尚無單位資料'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      單位代碼
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      地址
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      坪數
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      繳費方式
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      區權人
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 shadow-[-4px_0_4px_-2px_rgba(0,0,0,0.06)]">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {units.map((unit) => (
                    <tr key={unit.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openEditModal(unit, true)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                          title="檢視單位詳細資訊（唯讀）"
                        >
                          {unit.unitCode}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {unit.address}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {unit.pingArea} 坪
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {paymentMethodLabels[unit.paymentMethod]}
                        </span>
                        <span className="ml-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {paymentFrequencyLabels[unit.paymentFrequency]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const owners = (unit.unitContacts || []).filter(uc => uc.role === 'OWNER')
                          if (owners.length === 0) return <span className="text-sm text-gray-400">-</span>
                          return (
                            <div className="flex flex-wrap gap-1">
                              {owners.map(uc => (
                                <span
                                  key={uc.id}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800"
                                  title={uc.contact?.phone || ''}
                                >
                                  {uc.contact?.name || '(未命名)'}
                                </span>
                              ))}
                            </div>
                          )
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white z-10 shadow-[-4px_0_4px_-2px_rgba(0,0,0,0.06)]">
                        {canManage && (
                          <button
                            onClick={() => openEditModal(unit)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                            title="編輯"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(unit)}
                            className="text-red-600 hover:text-red-900"
                            title="刪除"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {unitsPagination && (
              <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between flex-wrap gap-y-2">
                <div className="text-sm text-gray-700">
                  共 {unitsPagination.total} 筆，第 {unitsPagination.page} / {unitsPagination.totalPages || 1} 頁
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-1">
                    <label className="text-sm text-gray-500">每頁</label>
                    <select
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
                      className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      {FINANCE_PAGE_SIZE_OPTIONS.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-500">筆</span>
                  </div>
                  {unitsPagination.totalPages > 1 && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeftIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setPage(p => Math.min(unitsPagination.totalPages, p + 1))}
                        disabled={page === unitsPagination.totalPages}
                        className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRightIcon className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 戶別操作紀錄 */}
      <FinanceAuditLog section="units" title="戶別操作紀錄" limit={50} />

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-lg bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editReadOnly
                  ? `檢視單位${activeUnit ? ` - ${activeUnit.unitCode}` : ''}`
                  : isEditing
                    ? `編輯單位${activeUnit ? ` - ${activeUnit.unitCode}` : ''}`
                    : '新增單位'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <fieldset disabled={editReadOnly} className="space-y-4 border-0 p-0 disabled:opacity-80">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  單位代碼 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.unitCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, unitCode: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="例如：A1-03F"
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    棟別 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.building}
                    onChange={(e) => setFormData(prev => ({ ...prev, building: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="A棟"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    樓層 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.floor}
                    onChange={(e) => setFormData(prev => ({ ...prev, floor: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="12"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    戶號
                  </label>
                  <input
                    type="text"
                    value={formData.unitNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, unitNumber: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="01"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  地址 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="台北市信義區..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  坪數 <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.pingArea}
                  onChange={(e) => setFormData(prev => ({ ...prev, pingArea: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="30.5"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    繳費方式
                  </label>
                  <select
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.entries(paymentMethodLabels).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    繳費週期
                  </label>
                  <select
                    value={formData.paymentFrequency}
                    onChange={(e) => setFormData(prev => ({ ...prev, paymentFrequency: e.target.value as PaymentFrequency }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.entries(paymentFrequencyLabels).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 編輯模式下顯示繳費人區塊 */}
              {isEditing && activeUnit && (() => {
                const owners = (contactsUnit?.unitContacts || activeUnit.unitContacts || []).filter(uc => uc.role === 'OWNER')
                const currentPayerId = contactsUnit?.payerId ?? activeUnit.payerId ?? null
                const payerMatchesOwner = !!currentPayerId && owners.some(o => o.contactId === currentPayerId)
                const handleToggleSingleOwner = async (checked: boolean) => {
                  if (!checked) {
                    // 不允許取消（繳費人不可為空）
                    showError('繳費人不能為空', '請改用「下方搜尋」指派其他繳費人；此欄位不能清空')
                    return
                  }
                  if (owners.length === 0) return
                  const only = owners[0]
                  if (only.contactId) await handleSetPayer(only.contactId)
                }
                return (
                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <h4 className="text-sm font-medium text-gray-900">繳費人</h4>

                    {owners.length === 0 && (
                      <span className="text-xs text-gray-400">未設定區權人，無法快速指派</span>
                    )}

                    {owners.length === 1 && (
                      <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={payerMatchesOwner}
                          disabled={payerMutating}
                          onChange={(e) => handleToggleSingleOwner(e.target.checked)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded disabled:opacity-50"
                        />
                        <span>繳費人同區權人（{owners[0].contact?.name || ''}）</span>
                      </label>
                    )}

                    {owners.length > 1 && (
                      <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                        <span className="text-xs text-gray-500">繳費人同區權人：</span>
                        {owners.map(o => {
                          const selected = currentPayerId === o.contactId
                          return (
                            <button
                              key={o.id}
                              type="button"
                              onClick={() => o.contactId && !selected && handleSetPayer(o.contactId)}
                              disabled={payerMutating || selected}
                              className={`px-2.5 py-1 text-xs rounded-full border ${
                                selected
                                  ? 'bg-indigo-100 border-indigo-500 text-indigo-800 cursor-default'
                                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                              } disabled:opacity-70`}
                              title={selected ? '目前繳費人（不可取消，須另行指派才能切換）' : `指定 ${o.contact?.name || ''} 為繳費人`}
                            >
                              {selected && '☑ '}
                              {o.contact?.name || '(未命名)'}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                  <div className="border border-gray-200 rounded-md p-3">
                    {(contactsUnit?.payer ?? activeUnit.payer) ? (
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          繳費人
                        </span>
                        <span className="text-sm font-medium text-gray-900">{(contactsUnit?.payer ?? activeUnit.payer)!.name}</span>
                        {(contactsUnit?.payer ?? activeUnit.payer)!.phone && (
                          <span className="text-xs text-gray-500">{(contactsUnit?.payer ?? activeUnit.payer)!.phone}</span>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-red-500 mb-2">⚠ 尚未指定繳費人，請於下方搜尋並指派</div>
                    )}
                    <div className="mt-2">
                      <input
                        type="text"
                        value={payerSearch}
                        onChange={(e) => setPayerSearch(e.target.value)}
                        placeholder="輸入姓名/電話搜尋要指定的繳費人..."
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {payerSearch && (
                        <div className="mt-1 max-h-40 overflow-y-auto border border-gray-200 rounded-md bg-white">
                          {contacts
                            .filter(c => {
                              const kw = payerSearch.toLowerCase()
                              return (
                                c.name.toLowerCase().includes(kw) ||
                                (c.phone || '').toLowerCase().includes(kw) ||
                                (c.idNumber || '').toLowerCase().includes(kw)
                              )
                            })
                            .slice(0, 20)
                            .map(c => (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => handleSetPayer(c.id)}
                                disabled={payerMutating}
                                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 disabled:opacity-50 ${(contactsUnit?.payerId ?? activeUnit.payerId) === c.id ? 'bg-blue-100' : ''}`}
                              >
                                <span className="font-medium">{c.name}</span>
                                {c.phone && <span className="ml-2 text-xs text-gray-500">{c.phone}</span>}
                                {(contactsUnit?.payerId ?? activeUnit.payerId) === c.id && <span className="ml-2 text-xs text-blue-700">(目前)</span>}
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                )
              })()}

              {/* 編輯模式下顯示關聯聯絡人管理區塊 */}
              {isEditing && contactsUnit && (
                <div className="border-t pt-4 space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">住戶 / 聯絡人（含區權人）</h4>

                  <div className="border border-gray-200 rounded-md">
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500">
                      目前關聯（{contactsUnit.unitContacts?.length || 0} 筆）
                    </div>
                    {contactsUnitLoading ? (
                      <div className="p-4 text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      </div>
                    ) : !contactsUnit.unitContacts || contactsUnit.unitContacts.length === 0 ? (
                      <div className="p-4 text-sm text-gray-500 text-center">尚無關聯聯絡人</div>
                    ) : (
                      <div className="divide-y divide-gray-200 max-h-64 overflow-y-auto">
                        {contactsUnit.unitContacts.map(uc => {
                          const today = new Date()
                          today.setHours(0, 0, 0, 0)
                          const end = uc.leaseEndDate ? new Date(String(uc.leaseEndDate).slice(0, 10)) : null
                          const expired = uc.role === 'TENANT' && end && end < today
                          const isEditing = leaseEditingId === uc.id
                          return (
                            <div key={uc.id} className="px-3 py-2 space-y-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ROLE_LABELS[uc.role].className}`}>
                                    {ROLE_LABELS[uc.role].label}
                                  </span>
                                  <span className="text-sm font-medium text-gray-900">{uc.contact?.name || '(未知聯絡人)'}</span>
                                  {uc.contact?.phone && (
                                    <span className="text-xs text-gray-500">{uc.contact.phone}</span>
                                  )}
                                  {uc.role === 'TENANT' && uc.leaseStartDate && uc.leaseEndDate && !isEditing && (
                                    <span className={`text-xs ${expired ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                      {expired && '⚠ 過期 · '}
                                      {String(uc.leaseStartDate).slice(0, 10)} ~ {String(uc.leaseEndDate).slice(0, 10)}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-1">
                                  {uc.role === 'TENANT' && !isEditing && (
                                    <button
                                      type="button"
                                      onClick={() => beginEditLease(uc)}
                                      disabled={contactMutating}
                                      className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50 px-1.5"
                                      title="編輯租約期間"
                                    >
                                      編輯租約
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveUnitContact(uc)}
                                    disabled={contactMutating}
                                    className="text-red-500 hover:text-red-700 disabled:opacity-50"
                                    title="移除此關聯"
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              {isEditing && (
                                <div className="flex items-center space-x-2 pl-2">
                                  <input
                                    type="date"
                                    value={leaseEditStart}
                                    onChange={(e) => setLeaseEditStart(e.target.value)}
                                    className="px-2 py-1 text-xs border border-gray-300 rounded"
                                  />
                                  <span className="text-xs text-gray-500">~</span>
                                  <input
                                    type="date"
                                    value={leaseEditEnd}
                                    onChange={(e) => setLeaseEditEnd(e.target.value)}
                                    className="px-2 py-1 text-xs border border-gray-300 rounded"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => saveEditLease(uc)}
                                    disabled={contactMutating}
                                    className="px-2 py-1 text-xs text-white bg-blue-600 rounded disabled:opacity-50"
                                  >
                                    儲存
                                  </button>
                                  <button
                                    type="button"
                                    onClick={cancelEditLease}
                                    disabled={contactMutating}
                                    className="px-2 py-1 text-xs text-gray-700 bg-gray-100 rounded disabled:opacity-50"
                                  >
                                    取消
                                  </button>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  <div className="border border-gray-200 rounded-md">
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500">新增關聯</div>
                    <div className="p-3 space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-500 mb-1">聯絡人</label>
                          <input
                            type="text"
                            value={contactSearch}
                            onChange={(e) => { setContactSearch(e.target.value); setNewContactId('') }}
                            placeholder="輸入姓名/電話搜尋..."
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          {contactSearch && (
                            <div className="mt-1 max-h-40 overflow-y-auto border border-gray-200 rounded-md bg-white">
                              {contacts
                                .filter(c => {
                                  const kw = contactSearch.toLowerCase()
                                  return (
                                    c.name.toLowerCase().includes(kw) ||
                                    (c.phone || '').toLowerCase().includes(kw) ||
                                    (c.idNumber || '').toLowerCase().includes(kw)
                                  )
                                })
                                .slice(0, 20)
                                .map(c => (
                                  <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => { setNewContactId(c.id); setContactSearch(c.name) }}
                                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 ${newContactId === c.id ? 'bg-blue-100' : ''}`}
                                  >
                                    <span className="font-medium">{c.name}</span>
                                    {c.phone && <span className="ml-2 text-xs text-gray-500">{c.phone}</span>}
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">角色</label>
                          <select
                            value={newContactRole}
                            onChange={(e) => setNewContactRole(e.target.value as ContactRole)}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {ROLE_OPTIONS.map(r => (
                              <option key={r} value={r}>{ROLE_LABELS[r].label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {newContactRole === 'TENANT' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">租約起始日 <span className="text-red-500">*</span></label>
                            <input
                              type="date"
                              value={newLeaseStart}
                              onChange={(e) => setNewLeaseStart(e.target.value)}
                              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">租約結束日 <span className="text-red-500">*</span></label>
                            <input
                              type="date"
                              value={newLeaseEnd}
                              onChange={(e) => setNewLeaseEnd(e.target.value)}
                              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      )}
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={handleAddUnitContact}
                          disabled={!newContactId || contactMutating}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          {contactMutating ? '處理中...' : '新增關聯'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              </fieldset>

              {/* 編輯紀錄（僅編輯模式） */}
              {isEditing && activeUnit && (
                <FinanceAuditLog entityType="Unit" entityId={activeUnit.id} compact limit={30} />
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  {editReadOnly ? '關閉' : '取消'}
                </button>
                {!editReadOnly && (
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {submitting ? '處理中...' : isEditing ? '更新' : '建立'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  )
}

export default UnitList
